// Phase grouping, phase state and progress counts, worked out in the browser
// because phases have no stored status by design.
//
// Progress counts only `action` items (decision 9): events, admin items and
// reviews are not "actions done". It is shown as counts, never as a bar or a
// percentage, because the spec forbids framing plan execution as progress
// toward the goal.

export const PHASE_STATES = {
  current: 'current',
  next: 'next',
  later: 'later',
  done: 'done',
};

export const PHASE_STATE_LABELS = {
  current: 'Current',
  next: 'Up next',
  later: 'Later',
  done: 'Done',
};

export const WORKLOAD_LABELS = {
  light: 'Light',
  moderate: 'Moderate',
  heavy: 'Heavy',
  peak: 'Peak',
};

const statusOf = (item) => item?.progress_status || 'pending';
const isAction = (item) => item?.item_type === 'action';

export function actionCounts(items) {
  const actions = (items || []).filter(isAction);
  return {
    done: actions.filter((i) => statusOf(i) === 'completed').length,
    total: actions.length,
  };
}

// "2 of 4"; "Dates only" when every item in the phase is an event; null when
// there are no actions but the phase is not only dates (nothing to count).
export function phaseCountLabel(items) {
  const list = items || [];
  const { done, total } = actionCounts(list);
  if (total > 0) return `${done} of ${total}`;
  if (list.length > 0 && list.every((i) => i.item_type === 'event')) return 'Dates only';
  return null;
}

export function pathProgressLabel(items) {
  const { done, total } = actionCounts(items);
  if (total === 0) return null;
  return `${done} of ${total} ${total === 1 ? 'action' : 'actions'} done`;
}

// Items grouped under their phases in phase order. Items whose phase_id does
// not match a phase (a response from before phases existed, or a partial
// payload) are kept in a trailing group rather than dropped.
export function groupItemsByPhase(phases, items) {
  const ordered = [...(Array.isArray(phases) ? phases : [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const known = new Set(ordered.map((p) => p.id));
  const groups = ordered.map((phase) => ({
    phase,
    items: (items || []).filter((i) => i.phase_id === phase.id),
  }));
  const orphans = (items || []).filter((i) => !known.has(i.phase_id));
  if (orphans.length > 0) groups.push({ phase: null, items: orphans });
  return groups;
}

// The current phase is the first with an unfinished (pending) action. Phases
// before it are done, the one after it is up next, the rest are later. When
// no phase has a pending action the plan's actions are all finished and every
// phase reads done.
export function phaseStates(groups) {
  const currentIndex = groups.findIndex(
    (g) => g.items.some((i) => isAction(i) && statusOf(i) === 'pending'),
  );
  return groups.map((_, index) => {
    if (currentIndex === -1 || index < currentIndex) return PHASE_STATES.done;
    if (index === currentIndex) return PHASE_STATES.current;
    if (index === currentIndex + 1) return PHASE_STATES.next;
    return PHASE_STATES.later;
  });
}

// One phase, or none at all, means no phase headings (decision 8): every
// migrated plan is a single "Plan" phase, and a heading reading "Plan" adds
// nothing.
export function showsPhaseHeadings(groups) {
  return groups.filter((g) => g.phase).length > 1;
}
